/**
 * Assistance Guide Rules
 *
 * Each rule defines a contextual popup that appears based on testator progress.
 * Rules are evaluated in priority order (lower number = higher priority).
 * Only the first matching rule is shown per page load.
 *
 * Every rule is shown at most once per 24h per user by default. Override the
 * window with `cooldownHours`, or set `repeatable: true` to bypass cooldown
 * entirely (always show when the condition matches).
 *
 * To add a new guide: append a rule object to the array below.
 *
 * Condition signature: (testator, steps, context) => boolean
 *   context: { allStepsCompleted, willDetailsChanged }
 *
 * Rules can use either static `content` or dynamic `contentFn(testator)`.
 */

import {
  latestWillVersion,
  latestUploadedWill,
  latestValidUploadedWill,
} from '../constants/js/will-verification';

function formatDate(dateString) {
  if (!dateString) return '';
  const d = new Date(dateString);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export default [
  // 1. All steps done, no will versions yet — first-time view
  {
    id: 'first-will-download',
    priority: 1,
    page: 'overview',
    condition: (testator, _steps, ctx) =>
      ctx.allStepsCompleted &&
      (!testator.willVersions || !testator.willVersions.length),
    contentFn: testator => {
      const languageCodes = testator?.company?.willDownloadLanguages || ['en'];
      const showLanguageSelect = languageCodes.length > 1;
      return {
        title: 'Next Steps',
        body: `Your will is now ready. Please download and sign it according to the instructions on the first page.
              Once signed, please upload it onto the platform for validation by our team.
              Upon validation of this version of your will we will replace your previous version`,
        actionText: 'Download will',
        actionType: 'event',
        actionEvent: 'trigger-download-will',
        showLanguageSelect,
        languageCodes,
        defaultLanguageCode: testator?.user?.locale || 'en',
      };
    },
  },

  // 2. All steps done, has will versions, details changed — updating existing will
  {
    id: 'will-details-changed',
    priority: 2,
    page: 'overview',
    condition: (testator, _steps, ctx) =>
      ctx.allStepsCompleted &&
      ctx.willDetailsChanged &&
      testator.willVersions &&
      testator.willVersions.length > 0,
    contentFn: testator => {
      const version = latestWillVersion(testator.willVersions);
      const willDate = version ? formatDate(version.createdAt) : '';
      const hasUploadedWill = !!latestUploadedWill(testator.willVersions);

      const content = {
        title: 'You Are Updating Your Information',
        body: `To make these updates part of your final valid will, please download the updated version of your will, sign it, and submit it for validation.

       Once this step is successfully completed, this new version will replace the previous version of your will, dated ${willDate}.`,
      };

      if (hasUploadedWill) {
        content.actionText = 'View uploaded will';
        content.actionType = 'event';
        content.actionEvent = 'view-latest-uploaded-will';
      } else {
        content.actionText = 'View latest will';
        content.actionType = 'event';
        content.actionEvent = 'view-latest-will';
      }

      return content;
    },
  },

  // 3a. All steps done, first will downloaded but not yet uploaded for verification
  {
    id: 'will-needs-upload',
    priority: 3,
    page: 'overview',
    condition: (testator, _steps, ctx) => {
      if (!ctx.allStepsCompleted) return false;
      if (!testator.willVersions || !testator.willVersions.length) return false;
      if (testator.willVersions.length > 1) return false;
      if (ctx.willDetailsChanged) return false;
      const uploaded = latestUploadedWill(testator.willVersions);
      return !uploaded;
    },
    content: {
      title: 'Next Step - Validate Will',
      body: `To ensure your will is valid we need you to sign the will and upload it to the platform for validation.

      Once the will is validated, you can store the will in our vault for safekeeping.`,
      actionText: 'Upload will',
      actionType: 'route',
      actionRoute: '/will-verification',
    },
  },

  // 3b. All steps done, updated will downloaded but not yet uploaded — returning user
  {
    id: 'will-needs-upload-updated',
    priority: 3,
    page: 'overview',
    condition: (testator, _steps, ctx) => {
      if (!ctx.allStepsCompleted) return false;
      if (!testator.willVersions || testator.willVersions.length <= 1)
        return false;
      if (ctx.willDetailsChanged) return false;
      const uploaded = latestUploadedWill(testator.willVersions);
      return !uploaded;
    },
    content: {
      title: 'Upload To Validate New Will',
      body: `You can now upload your new will. Once validated, your new will version, will replace your previous will

      **Are you sure you want to replace your existing will?**`,
      actionText: 'Yes, upload my new will',
      actionType: 'route',
      actionRoute: '/will-verification',
    },
  },

  // 4. Will successfully uploaded on will-verification page.
  // Always show on every successful upload — bypasses the 24h cooldown.
  {
    id: 'will-upload-success',
    priority: 1,
    page: 'will-verification',
    repeatable: true,
    condition: (_testator, _steps, ctx) => ctx.willUploadSuccess,
    contentFn: testator => {
      const hasValidatedWill = !!latestValidUploadedWill(
        testator.willVersions || []
      );
      const body = hasValidatedWill
        ? `We will send you an email once the will has been validated.

      Your most recent validated will is the will that will be used in the event of your passing.`
        : `We will send you an email once your will has been validated.`;
      return {
        title: 'Upload Successful',
        body,
        actionType: 'dismiss',
      };
    },
  },

  // 5. Will is valid but location/vault not set
  {
    id: 'will-valid-set-location',
    priority: 4,
    page: 'overview',
    condition: (testator, _steps, ctx) => {
      if (!ctx.allStepsCompleted) return false;
      if (ctx.willDetailsChanged) return false;
      if (!testator.willVersions || !testator.willVersions.length) return false;
      const version = latestWillVersion(testator.willVersions);
      return (
        version &&
        version.status === 'VALID' &&
        !testator.willLocation &&
        !testator.dateStored &&
        !testator.activeWillCollectionBooking
      );
    },
    content: {
      title: 'Your Will Is Validated',
      body: `We have successfully validated your will.

      You can now set the location and store the will in our vault for safekeeping.

      We recommend storing it in our vault for easy access by your Executor when needed.`,
      actionText: 'Set your will location',
      actionType: 'route',
      actionRoute: '/will-location',
    },
  },

  // 6. Will failed validation — invalid or inconclusive
  {
    id: 'will-failed-validation',
    priority: 4,
    page: 'overview',
    condition: (testator, _steps, ctx) => {
      if (!ctx.allStepsCompleted) return false;
      if (ctx.willDetailsChanged) return false;
      if (!testator.willVersions || !testator.willVersions.length) return false;
      const version = latestWillVersion(testator.willVersions);
      return (
        version &&
        (version.status === 'INVALID' ||
          version.status === 'VALIDITY_INCONCLUSIVE')
      );
    },
    content: {
      title: 'Your Will Failed Validation',
      body: `Your last uploaded will does not comply with the requirements of a valid will. 
      Please review the feedback provided in the email we sent to you.`,
      actionText: 'Re-upload your will',
      actionType: 'route',
      actionRoute: '/will-verification',
    },
  },

  // 7. Will valid, location set, but no collection scheduled
  {
    id: 'will-schedule-collection',
    priority: 6,
    page: 'overview',
    condition: (testator, _steps, ctx) => {
      if (!ctx.allStepsCompleted) return false;
      if (ctx.willDetailsChanged) return false;
      if (!testator.willVersions || !testator.willVersions.length) return false;
      const version = latestWillVersion(testator.willVersions);
      return (
        version &&
        version.status === 'VALID' &&
        testator.willLocation &&
        !testator.dateStored &&
        !testator.activeWillCollectionBooking
      );
    },
    contentFn: testator => ({
      title: 'Store Your Will - Set Up A Collection Date',
      body: `We collect and store your will at no cost to you. Follow the prompts on the following page to arrange collection of your will.`,
      actionText: 'Schedule collection',
      actionType: 'route',
      actionRoute: `/will-collection-booking?testatorId=${testator.id}`,
    }),
  },

  // 8. Will collection booked — show collection date
  {
    id: 'will-collection-booked',
    priority: 5,
    page: 'overview',
    condition: testator => {
      const booking = testator.activeWillCollectionBooking;
      return (
        booking &&
        booking.collectionStatus === 'collection-assigned' &&
        booking.collectionDate
      );
    },
    contentFn: testator => {
      const date = formatDate(
        testator.activeWillCollectionBooking.collectionDate
      );
      return {
        title: 'Will Collection Booked',
        body: `Your will collection is booked for –\n${date}

An email reminder will be sent the day before collection.`,
        actionType: 'dismiss',
      };
    },
  },

  // 9. Will stored in vault and valid — fully complete
  {
    id: 'will-complete',
    priority: 7,
    page: 'overview',
    condition: (testator, _steps, ctx) => {
      if (!ctx.allStepsCompleted) return false;
      if (ctx.willDetailsChanged) return false;
      return !!testator.dateStored && testator.willValid === true;
    },
    content: {
      title: 'Congratulations',
      body: `Your will has now been validated and safely stored. This is the will that we'll use in the event of your passing.

      If you wish to make any changes to your Will, you can do this on the platform and upload the revised version for validation.`,
      actionType: 'dismiss',
    },
  },
];
